import { useCallback } from "react";
import { useStytch } from "@stytch/react";
import { freshSessionJwt } from "@/lib/stytch-session";

/**
 * Returns a token factory that yields a fresh Stytch session JWT.
 *
 * The previous version read `session.getTokens()` and said the SDK "refreshes
 * the session transparently in the background, so we never need to check
 * expiry — it's guaranteed fresh". That is the assumption that made sessions
 * look far shorter than they are: the SDK's refresh is a timer, and browsers
 * throttle timers in backgrounded tabs and suspend them on sleep, so the
 * cached JWT can be expired precisely when this is called.
 *
 * Which matters most here of all. SignalR calls this on reconnect — after
 * exactly the sleep that leaves the token stale — and unlike a fetch, it has no
 * 401 retry to fall back on.
 */
export function useValidToken(): () => Promise<string | undefined> {
  const stytch = useStytch();

  return useCallback(async (): Promise<string | undefined> => {
    return freshSessionJwt(stytch as never);
  }, [stytch]);
}
